import type { BrokerCostProfile } from "@/lib/calculators/brokerFees";

const EDITOR = "Алекс В.";
const CHECKED = "2026-08-15";

/**
 * Числовите профили за разходния модел. Всяка стойност идва от същия
 * официален източник, който е записан в централния registry
 * (src/data/providers/brokers.ts) - тук е само машинно четимата ѝ форма.
 *
 * `supports` показва за коя валута на актива брокерът има ПОТВЪРДЕНА
 * тарифа. Interactive Brokers участва само в USD сценария, защото
 * потвърдената в registry комисиона е за US листинг ($0.005/акция,
 * мин. $1); европейската тарифа не е потвърдена и не се моделира.
 *
 * Курс за преизчисляване на тарифи в USD: 1 EUR = 1.08 USD (закръглено
 * допускане, посочено видимо на страницата).
 */
export const USD_PER_EUR = 1.08;

const usdToEur = (usd: number) => Math.round((usd / USD_PER_EUR) * 100) / 100;

export const brokerCostProfiles: BrokerCostProfile[] = [
  {
    slug: "trading-212",
    name: "Trading 212",
    accountCurrency: "EUR",
    commissionPct: 0,
    commissionFixedEur: 0,
    commissionMinEur: 0,
    fxFeePct: 0.15,
    inactivityEurPerMonth: 0,
    supports: ["EUR", "USD"],
    sourceUrl: "https://www.trading212.com/terms/invest",
    verifiedAt: CHECKED,
    verifiedBy: EDITOR,
    notes: "0% комисиона; валутна конверсия 0.15% при актив в чужда валута.",
  },
  {
    slug: "etoro",
    name: "eToro",
    accountCurrency: "USD",
    commissionPct: 0,
    commissionFixedEur: usdToEur(1),
    commissionMinEur: usdToEur(1),
    fxFeePct: 0.5,
    inactivityEurPerMonth: 0,
    supports: ["EUR", "USD"],
    sourceUrl: "https://www.etoro.com/trading/fees/",
    verifiedAt: CHECKED,
    verifiedBy: EDITOR,
    notes:
      "Комисионата е $1 или $2 според държава и борса - моделът използва по-ниската публикувана стойност ($1). Сметката е в USD, затова покупка на актив в EUR минава през конверсия 0.5%.",
  },
  {
    slug: "freedom24",
    name: "Freedom24",
    accountCurrency: "EUR",
    commissionPct: 0,
    commissionFixedEur: 2,
    commissionMinEur: 2,
    fxFeePct: 0.5,
    inactivityEurPerMonth: 0,
    supports: ["EUR", "USD"],
    sourceUrl: "https://freedom24.com/tariffs",
    verifiedAt: CHECKED,
    verifiedBy: EDITOR,
    notes:
      "€2 на ордер плюс $0.02 на акция. Моделът включва само фиксираните €2, защото броят акции зависи от цената на конкретния инструмент - реалният разход е по-висок.",
  },
  {
    slug: "interactive-brokers",
    name: "Interactive Brokers (IBIE)",
    accountCurrency: "EUR",
    commissionPct: 0,
    commissionFixedEur: usdToEur(1),
    commissionMinEur: usdToEur(1),
    fxFeePct: 0.002,
    fxMinEur: usdToEur(2),
    inactivityEurPerMonth: 0,
    supports: ["USD"],
    sourceUrl:
      "https://www.interactivebrokers.ie/en/pricing/commissions-home.php",
    verifiedAt: CHECKED,
    verifiedBy: EDITOR,
    notes:
      "Моделира се минималната комисиона от $1 за US листинг. Spot FX е 0.002% с минимум $2 на сделка - при малки суми минимумът доминира и моделът начислява именно него.",
  },
];

export function getCostProfile(slug: string) {
  return brokerCostProfiles.find((p) => p.slug === slug);
}
